import { useMemo, useState } from "react";
import { RowSelectionState } from "react-table-8.10.7";
import { useGetAllBillsQuery } from "store/apis/vendorBills";
import { BILL_STATUS, FINANCIAL_RECORD_TYPE } from "constants/vendorBills";
import { REVENUE_DATA_SOURCE } from "constants/revenueDataSource";

interface UseBillBulkActionProps {
  groupId: string;
  entityId: string;
  selectedRows: string[];
  rowSelection?: RowSelectionState;
  actionType: "markAsActive" | "delete";
}

export const useBillBulkAction = ({
  groupId,
  entityId,
  selectedRows,
  rowSelection,
  actionType,
}: UseBillBulkActionProps) => {
  const { data: allBills, isLoading } = useGetAllBillsQuery(
    {
      groupId,
      entityId,
      limit: selectedRows.length,
      ids: selectedRows.join(","),
      bill_type: FINANCIAL_RECORD_TYPE.BILL,
      search_term: "",
    },
    {
      skip: !groupId || !entityId || !selectedRows.length,
    }
  );

  const { bills = [] } = allBills || {};

  const [rowSelectionState, setRowSelectionState] = useState(
    rowSelection || {}
  );

  const newSelectedRows = useMemo(
    () =>
      Object.entries(rowSelectionState)
        .filter(([_, isSelected]) => isSelected)
        .map(([uuid]) => uuid),
    [rowSelectionState]
  );

  const valid = bills.filter((row) => {
    const isSelected = newSelectedRows.includes(row.uuid);
    const isDraft = row.bill_payment_status === BILL_STATUS.DRAFT;
    const isNotStripe = row.source !== REVENUE_DATA_SOURCE.STRIPE;

    return isDraft && isNotStripe && isSelected;
  });

  const invalid = bills.filter((row) => {
    const isNotStripe = row.source !== REVENUE_DATA_SOURCE.STRIPE;

    return row.bill_payment_status !== BILL_STATUS.DRAFT || !isNotStripe;
  });

  const totalBills = bills.length;
  const isAllBillInvalid = valid.length === 0;

  const shouldShowButton = valid.length > 0 && newSelectedRows.length !== 0;

  return {
    allBills,
    bills,
    isLoading,
    rowSelectionState,
    setRowSelectionState,
    newSelectedRows,
    valid,
    invalid,
    totalBills,
    isAllBillInvalid,
    shouldShowButton,
  };
};
